import { useState } from 'react'
import type { PlantWithSections } from '../lib/types'
import type { PermissionService } from '../services/permissions'
import { supabase } from '../lib/supabase'

const UTILITY_LIMIT = 5000
const TELEMETRY_LIMIT = 5000
const MAINTENANCE_LIMIT = 1000

export function Export({ permissions, plants }: { permissions: PermissionService; plants: PlantWithSections[] }) {
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  const canBackup = permissions.isEngineer || permissions.isAdmin
    || plants.some((plant) => plant.sections.some((section) => permissions.can(section.id, 'can_backup')))

  if (!canBackup) return <div className="panel"><h3>Export & Backup</h3><p>Your account does not have backup permission for an authorized section.</p></div>

  const run = async () => {
    setBusy(true); setMessage(''); setError('')
    try {
      let utilityQuery = supabase.from('utility_daily_readings').select('*').order('reading_date', { ascending: false }).limit(UTILITY_LIMIT)
      let telemetryQuery = supabase.from('telemetry_readings').select('*').order('recorded_at', { ascending: false }).limit(TELEMETRY_LIMIT)
      let maintenanceQuery = supabase.from('maintenance_records').select('*').order('maintenance_date', { ascending: false }).limit(MAINTENANCE_LIMIT)

      if (!permissions.isEngineer && !permissions.isAdmin) {
        const backupSectionIds = plants.flatMap((plant) => plant.sections.filter((section) => permissions.can(section.id, 'can_backup')).map((section) => section.id))
        if (!backupSectionIds.length) throw new Error('No authorized section is available for backup.')
        utilityQuery = utilityQuery.in('section_id', backupSectionIds)

        const { data: fields, error: fieldError } = await supabase.from('fields').select('id').in('section_id', backupSectionIds).eq('enabled', true)
        if (fieldError) throw new Error(`Unable to resolve backup fields: ${fieldError.message}`)
        const fieldIds = (fields ?? []).map((field) => Number(field.id)).filter(Number.isFinite)
        telemetryQuery = fieldIds.length ? telemetryQuery.in('field_id', fieldIds) : telemetryQuery.in('field_id', [-1])

        const { data: equipment, error: equipmentError } = await supabase.from('equipment').select('id').in('section_id', backupSectionIds).eq('enabled', true)
        if (equipmentError) throw new Error(`Unable to resolve backup equipment: ${equipmentError.message}`)
        const equipmentIds = (equipment ?? []).map((item) => Number(item.id)).filter(Number.isFinite)
        maintenanceQuery = equipmentIds.length ? maintenanceQuery.in('equipment_id', equipmentIds) : maintenanceQuery.in('equipment_id', [-1])
      }

      const [utilityResult, telemetryResult, maintenanceResult] = await Promise.all([utilityQuery, telemetryQuery, maintenanceQuery])
      if (utilityResult.error) throw new Error(`Unable to export utility readings: ${utilityResult.error.message}`)
      if (telemetryResult.error) throw new Error(`Unable to export telemetry readings: ${telemetryResult.error.message}`)
      if (maintenanceResult.error) throw new Error(`Unable to export maintenance records: ${maintenanceResult.error.message}`)

      const payload = {
        schema_version: 'v7-operational-export-1',
        exported_at: new Date().toISOString(),
        limits: { utility_daily_readings: UTILITY_LIMIT, telemetry_readings: TELEMETRY_LIMIT, maintenance_records: MAINTENANCE_LIMIT },
        scope: permissions.isEngineer || permissions.isAdmin ? 'elevated' : 'operator-backup-sections',
        utility_daily_readings: utilityResult.data ?? [],
        telemetry_readings: telemetryResult.data ?? [],
        maintenance_records: maintenanceResult.data ?? [],
      }
      const json = JSON.stringify(payload, null, 2)
      const blob = new Blob([json], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = `apl-connect-export-${new Date().toISOString().slice(0, 10)}.json`; a.click()
      window.setTimeout(() => URL.revokeObjectURL(url), 0)
      setMessage(`Export created successfully (${json.length.toLocaleString()} bytes).`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to create export.')
    } finally { setBusy(false) }
  }

  return <div>
    <div className="head"><div><div className="eyebrow">DATA MANAGEMENT</div><h2>Export & Backup</h2><p>Create a bounded local JSON backup of operational datasets available to your account.</p></div></div>
    {message && <div className="message success">{message}</div>}
    {error && <div className="message error">{error}</div>}
    <section className="panel">
      <div className="panel-heading"><div><div className="eyebrow">LOCAL EXPORT</div><h3>Operational backup</h3></div><span className="pill light">Permission scoped</span></div>
      <p>Exports up to {UTILITY_LIMIT.toLocaleString()} utility readings, {TELEMETRY_LIMIT.toLocaleString()} telemetry readings and {MAINTENANCE_LIMIT.toLocaleString()} maintenance records. Operator exports are restricted to sections with backup permission; Supabase RLS remains authoritative.</p>
      <button className="primary" disabled={busy} onClick={() => void run()}>{busy ? 'Preparing…' : 'Create JSON backup'}</button>
    </section>
  </div>
}
